import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import TlogGrid from "./tlog_grid";
import { passViewHelper } from "../../helpers/passzone/pass-zone-helper";
import { extractZoneAndPassForM500FromTableLimits } from "../../helpers/tlog/tlog-data-mapper";
import { tlogHelper } from "../../helpers/tlog/tlog-helper";
import { FETCH_TLOG_DATA_SAGA } from "../../redux/data360LiveTlog/types";
import { getLimits } from "../../redux/data360Live_StatusConfiguration/action";
import ErrorBanner from "../Common/ErrorBanner";

// Utility functions
const formatTime = (timestamp) => {
  return timestamp ? new Date(timestamp * 1000).toLocaleTimeString() : "";
};

const getPassName = (tlogItem) => {
  let passName = tlogItem?.pass_name;

  if (tlogItem?.torch === "TRAIL") {
    passName = tlogItem?.zone_name;
  }

  if (passName?.includes("Head")) {
    passName = "HEAD";
  }

  return passName;
};

const hideColumnsStationZero = [
  "tilt",
  "distance",
  "wireSpeed",
  "oscillationWidth",
  "target",
  "horizontalBias",
  "frequency",
  "totalWireConsumed",
  "trueEnergy",
];

const TlogTableView = ({
  s_deviceid_event,
  station_number,
  type,
  zone,
  weld_number,
  setSelectedRowKeys,
  selectedRowKeys,
  boundary,
  tilt_start,
  tilt_end,
  isM500,
  rawZoneView,
  view,
  statusCalculationMethod,
  torch,
  rawZone,
  isFromPQRTab,
}) => {
  const dispatch = useDispatch();
  console.log("first", type, zone, weld_number, boundary, isM500, rawZoneView);
  const tlogState = useSelector((state) => state.data360LiveTlogReducer);
  const currentProject = useSelector(
    (state) => state.data360LiveProjectReducer?.currentProject || {}
  );
  const tableLimit = tlogState?.data?.tableLimit || [];
  // Get status configuration data
  const statusConfigState = useSelector(
    (state) => state.data360LiveStatusConfigReducer
  );
  const statusTableLimitData = statusConfigState?.limits?.allDataSet || [];

  // Data extraction
  const rows = tlogState?.data?.data || [];
  const conversionFactors = tlogState?.data?.conversionFactors || {};
  const decimalPlaces = tlogState?.data?.decimalPlaces || {};
  const weldParameterErrors = tlogState?.data?.weldParameterErrors;
  const isLoading = tlogState?.isLoading || false;

  const applyConversionFactor = (value, factor, fieldName) => {
    if (!value || value === "") return "";
    const numValue = parseFloat(value);
    const decimalPlacesForField = decimalPlaces?.[fieldName];
    return isNaN(numValue)
      ? value
      : (numValue * (factor || 1)).toFixed(decimalPlacesForField);
  };

  useEffect(() => {
    if (currentProject.project_id && currentProject.project_name) {
      const payload = {
        project_id: currentProject.project_id,
        project_name: currentProject.project_name,
        welduid_s_event_torch: s_deviceid_event,
        view_type: view,
        torch_type: torch,
        ...(rawZone && { zone_name: rawZone }),
        ...(tilt_start === 0 || tilt_start ? { tilt_start } : {}),
        ...(tilt_end && { tilt_end }),
      };

      dispatch({
        type: FETCH_TLOG_DATA_SAGA,
        payload,
      });
    }
  }, [
    dispatch,
    s_deviceid_event,
    currentProject,
    view,
    torch,
    rawZone,
    tilt_start,
    tilt_end,
  ]);

  // Fetch status configuration data
  useEffect(() => {
    if (currentProject.project_id) {
      const params = {
        project_number: currentProject.project_id,
      };
      dispatch(getLimits(params));
    }
  }, [dispatch, currentProject.project_id]);

  // Memoized grid rows transformation
  const gridRows = useMemo(() => {
    const showRedCircle =
      statusCalculationMethod ===
      passViewHelper.statusCalculationMethod.instantaneous;

    return rows.map((row, index) => {
      const isM500 =
        row?.welding_system?.toLowerCase() == "m500" ? true : false;

      const m500ZoneData = isM500
        ? extractZoneAndPassForM500FromTableLimits(
            tableLimit,
            row.job_number,
            row.zone_name
          )
        : {};

      return {
        sl_no: index + 1,
        statusNew: row.instantaneous_status,
        event: row.event,
        jobNumber: row.job_number,
        time: formatTime(row.time),
        tilt: row.tilt != null ? row.tilt : "",
        pipe: isM500 ? m500ZoneData.PipeNum : "-",
        pass: isM500 ? m500ZoneData.PassNum : row.pass_name || "",
        zone: isM500 ? m500ZoneData.ZoneNum : row.zone_name || "",
        distance: applyConversionFactor(
          row.distance,
          conversionFactors?.distance,
          "distance"
        ),
        travelSpeed: `${applyConversionFactor(
          row.travel_speed,
          conversionFactors?.travel_speed,
          "travel_speed"
        )}${
          showRedCircle && row.travel_speed_status === false ? "-color" : ""
        }`,
        volts: `${applyConversionFactor(
          row.volts,
          conversionFactors?.volts,
          "volts"
        )}${showRedCircle && row.volts_status === false ? "-color" : ""}`,
        current: `${applyConversionFactor(
          row.current,
          conversionFactors?.current,
          "current"
        )}${showRedCircle && row.current_status === false ? "-color" : ""}`,
        wireSpeed: `${applyConversionFactor(
          row.wire_speed,
          conversionFactors?.wire_speed,
          "wire_speed"
        )}${showRedCircle && row.wire_speed_status === false ? "-color" : ""}`,
        oscillationWidth: `${applyConversionFactor(
          row.oscillation_width,
          conversionFactors?.oscillation_width,
          "oscillation_width"
        )}${
          showRedCircle && row.oscillation_width_status === false
            ? "-color"
            : ""
        }`,
        target: `${applyConversionFactor(
          row.target,
          conversionFactors?.target,
          "target"
        )}${showRedCircle && row.target_status === false ? "-color" : ""}`,
        horizontalBias: `${applyConversionFactor(
          row.horizontal_bias,
          conversionFactors?.horizontal_bias,
          "horizontal_bias"
        )}${
          showRedCircle && row.horizontal_bias_status === false ? "-color" : ""
        }`,
        frequency: `${applyConversionFactor(
          row.frequency,
          conversionFactors?.frequency,
          "frequency"
        )}${showRedCircle && row.frequency_status === false ? "-color" : ""}`,
        totalWireConsumed: `${applyConversionFactor(
          row.total_wire_consumed,
          conversionFactors?.total_wire_consumed,
          "total_wire_consumed"
        )}${
          showRedCircle && row.total_wire_consumed_status === false
            ? "-color"
            : ""
        }`,
        trueEnergy: `${applyConversionFactor(
          row.true_energy,
          conversionFactors?.true_energy,
          "true_energy"
        )}${showRedCircle && row.true_energy_status === false ? "-color" : ""}`,
        heat: `${applyConversionFactor(
          row.heat,
          conversionFactors?.heat,
          "heat"
        )}${showRedCircle && row.heat_status === false ? "-color" : ""}`,
        headAngle:
          applyConversionFactor(
            row.head_angle,
            conversionFactors?.head_angle,
            "head_angle"
          ) || "",
        arcImpedance:
          applyConversionFactor(
            row.arc_impedance,
            conversionFactors?.arc_impedance,
            "arc_impedance"
          ) || "",
      };
    });
  }, [rows, conversionFactors, decimalPlaces, statusCalculationMethod]);

  // Memoized range calculation
  const getRange = useCallback(
    ({ tlogItem, limitData, reportColumns, column, rawColumnValue }) => {
      if (!tlogItem?.pass_name) {
        return { doesExist: false, showColor: "" };
      }

      const passName = getPassName(tlogItem);
      const job_number = tlogItem?.job_number;

      let tableLimitFiltered = job_number
        ? limitData?.filter((tbLimit) => tbLimit.job_number === job_number)
        : [];

      tableLimitFiltered = tableLimitFiltered?.filter(
        (tbLimit) => tbLimit.zone_name === passName
      );

      const uniquePassNames = [
        ...new Set(tableLimitFiltered?.map((ite) => ite.pass_name)),
      ];

      if (uniquePassNames?.length === 0) {
        return { doesExist: false, showColor: "" };
      }

      return {
        doesExist: true,
        showColor: `[${tlogHelper.rangeCalculation(
          tableLimitFiltered?.[0]?.[`${column}_min`],
          rawColumnValue,
          reportColumns,
          false
        )} -  ${tlogHelper.rangeCalculation(
          tableLimitFiltered?.[0]?.[`${column}_max`],
          rawColumnValue,
          reportColumns,
          false
        )}]`,
      };
    },
    []
  );

  const hasValidM500Data = useMemo(() => {
    return gridRows.some(
      (row) =>
        row.headAngle !== "" ||
        row.arcImpedance !== "" ||
        (row.pipe && row.pipe !== "-")
    );
  }, [gridRows]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Extract error messages from weldParameterErrors
  const getErrorMessages = () => {
    if (
      !weldParameterErrors?.errors ||
      weldParameterErrors.errors.length === 0
    ) {
      return [];
    }

    const errorMessages = [];
    weldParameterErrors.errors.forEach((error) => {
      if (error.parsedErrors?.errorMessages) {
        errorMessages.push(...error.parsedErrors.errorMessages);
      }
    });

    return errorMessages;
  };

  const errorMessages = getErrorMessages();

  return isFromPQRTab ? (
    <div>
      <TlogGrid
        rows={gridRows}
        loading={isLoading}
        statusCalculationMethod={statusCalculationMethod}
        getRange={getRange}
        tableLimitData={
          statusTableLimitData.length > 0 ? statusTableLimitData : tableLimit
        }
        rawTlogData={tlogState?.data?.data}
        currentProject={currentProject}
        hasValidM500Data={hasValidM500Data}
        isM500={hasValidM500Data}
        station_number={station_number}
        hideColumnsStationZero={hideColumnsStationZero}
        showRowSelection={true}
        selectedRowKeys={selectedRowKeys}
        onRowSelected={onSelectChange}
        tableHeight={-80}
        isFromPQRTab={isFromPQRTab}
      />
    </div>
  ) : (
    <div>
      <ErrorBanner errors={errorMessages} />
      <TlogGrid
        rows={gridRows}
        loading={isLoading}
        tableHeight={-240}
        statusCalculationMethod={statusCalculationMethod}
        getRange={getRange}
        tableLimitData={
          statusTableLimitData.length > 0 ? statusTableLimitData : tableLimit
        }
        rawTlogData={tlogState?.data?.data}
        currentProject={currentProject}
        hasValidM500Data={hasValidM500Data}
        isM500={hasValidM500Data}
        station_number={station_number}
        hideColumnsStationZero={hideColumnsStationZero}
        isFromPQRTab={isFromPQRTab}
      />
    </div>
  );
};

export default TlogTableView;
